import { calculatePercentChange } from 'src/utils/app';
import {
  ContentMetricType,
  ContentScores
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import {
  ChartDataAdapter,
  KeyMetric,
  KeyMetricScoreTitle,
  KeyMetricTitle
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';

export interface KeyMetricsData {
  primaryOverallScore: number;
  secondaryOverallScore?: number;
  primaryData: KeyMetric[];
  secondaryData?: Omit<KeyMetric, 'change'>[];
  topEntityPrimaryData: [string, number][];
  topEntitySecondaryData: [string, number][];
}

const CONTENT_TYPE_TO_OVERALL_SCORE = {
  [ContentMetricType.ContentScore]: 'contentScore',
  [ContentMetricType.ContentUtilizationScore]: 'contentUtilizationScore',
  [ContentMetricType.ContentAccuracyScore]: 'contentAccuracyScore'
};

export class KeyMetricsDataAdapter implements ChartDataAdapter<KeyMetricsData> {
  mainKeyMetrics: ContentScores;

  comparisonKeyMetrics: ContentScores;

  metricType: ContentMetricType;

  constructor(mainKeyMetrics: ContentScores, comparisonKeyMetrics: ContentScores, metricType: ContentMetricType) {
    this.mainKeyMetrics = mainKeyMetrics;
    this.comparisonKeyMetrics = comparisonKeyMetrics;
    this.metricType = metricType;
  }

  transform(): KeyMetricsData {
    const getChange = (metricName: string) =>
      calculatePercentChange(this.mainKeyMetrics[metricName], this.comparisonKeyMetrics[metricName]);

    const primaryData = Object.entries(this.mainKeyMetrics).map(([metricName, value]) => ({
      id: metricName,
      name: KeyMetricTitle[metricName],
      value,
      change: getChange(metricName)
    }));

    const secondaryData = Object.entries(this.comparisonKeyMetrics).map(([metricName, value]) => ({
      id: metricName,
      name: KeyMetricTitle[metricName],
      value
    }));
    const topEntityPrimaryData =
      Object.entries(this.mainKeyMetrics).map(([metricName, value]) => [KeyMetricScoreTitle[metricName], value]) ?? [];

    const topEntitySecondaryData =
      Object.entries(this.comparisonKeyMetrics).map(([metricName, value]) => [
        KeyMetricScoreTitle[metricName],
        value
      ]) ?? [];

    const primaryOverallScore = primaryData.find(
      (metric) => metric.id === CONTENT_TYPE_TO_OVERALL_SCORE[this.metricType]
    )?.value;
    const secondaryOverallScore = secondaryData.find(
      (metric) => metric.id === CONTENT_TYPE_TO_OVERALL_SCORE[this.metricType]
    )?.value;

    return {
      primaryOverallScore,
      secondaryOverallScore,
      primaryData,
      secondaryData,
      topEntityPrimaryData,
      topEntitySecondaryData
    };
  }
}

export class KeyMetricsFilterAdapter implements ChartDataAdapter<KeyMetricsData> {
  keyMetricsDataAdapter: KeyMetricsDataAdapter;

  keyMetricsMap: Record<string, string>;

  constructor(keyMetricsDataAdapter: KeyMetricsDataAdapter, keyMetricsMap: Record<string, string>) {
    this.keyMetricsDataAdapter = keyMetricsDataAdapter;
    this.keyMetricsMap = keyMetricsMap;
  }

  transform() {
    const {
      primaryOverallScore,
      secondaryOverallScore,
      primaryData,
      secondaryData,
      topEntityPrimaryData,
      topEntitySecondaryData
    } = this.keyMetricsDataAdapter.transform();
    const keys = Object.keys(this.keyMetricsMap);

    const filterMetric = (metric: KeyMetric) => keys.includes(metric.id);

    const substituteMetricName = (metric: KeyMetric) => ({
      ...metric,
      name: this.keyMetricsMap[metric.id] || metric.name
    });

    return {
      topEntityPrimaryData,
      topEntitySecondaryData,
      primaryOverallScore,
      secondaryOverallScore,
      primaryData: primaryData.filter(filterMetric).map(substituteMetricName),
      secondaryData: secondaryData.filter(filterMetric).map(substituteMetricName)
    };
  }
}

/**
 * Adapter class for transforming key metrics data for a single period.
 *
 * This class implements the ChartDataAdapter interface and is responsible for
 * adapting the key metrics data to a format suitable for visualization in charts.
 * It utilizes a KeyMetricsFilterAdapter to filter and transform the key metrics
 * data based on a predefined mapping.
 *
 * The `transform` method retrieves the primary overall score and primary data
 * from the KeyMetricsFilterAdapter. It then modifies the primary data to remove
 * the change property, as this adapter is focused on single period metrics
 * without comparative changes.
 *
 * @class SinglePeriodKeyMetricsAdapter
 * @implements {ChartDataAdapter<KeyMetricsData>}
 */
export class SinglePeriodKeyMetricsAdapter implements ChartDataAdapter<KeyMetricsData> {
  keyMetricsFilterAdapter: KeyMetricsFilterAdapter;

  constructor(keyMetricsFilterAdapter: KeyMetricsFilterAdapter) {
    this.keyMetricsFilterAdapter = keyMetricsFilterAdapter;
  }

  transform() {
    const { primaryOverallScore, primaryData } = this.keyMetricsFilterAdapter.transform();

    return {
      primaryOverallScore,
      primaryData: primaryData.map((metric) => ({
        ...metric,
        change: undefined
      }))
    };
  }
}
